import React, {useRef, useState} from 'react';
import {PhasePill} from './PhasePill';
import {DomainDetail} from './DomainDetail';
import {useDashboard} from './context';
import {COMPLEXITY_META, FUND_MAP, H_M_L, complexityMeta, effortSize, hasStrategyContext, quadrantOf, scToPlot} from './scoring';
import type {ComplexityCode, Domain, MatrixPoint} from './types';

/** Disruption × Readiness prioritization scatter. Ported verbatim from the prototype. */
export function MatrixView(): React.ReactElement {
  const {phases, phaseColors, serviceAreas, domains} = useDashboard();
  const [filterArea, setFilterArea] = useState('All');
  const [filterDomain, setFilterDomain] = useState('All');
  const [filterPhase, setFilterPhase] = useState('All');
  const [filterApproach, setFilterApproach] = useState('All');
  const [filterQuadrant, setFilterQuadrant] = useState('All');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState({x: 0, y: 0});
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Deterministic jitter: spread overlapping dots at same (x,y) using index-based angle.
  function jitterOffset(index: number, total: number): {dx: number; dy: number} {
    if (total <= 1) return {dx: 0, dy: 0};
    const angle = (index / total) * 2 * Math.PI;
    const spread = Math.min(total * 3, 14);
    return {dx: Math.cos(angle) * spread, dy: Math.sin(angle) * spread};
  }

  const CHART_SIZE = 780;
  const PADDING = 78;
  const PLOT_SIZE = CHART_SIZE - PADDING * 2;
  const toPixel = (val: number) => (val / 6) * PLOT_SIZE;

  const allPoints = domains.flatMap(d => d.jtbds.map(j => scToPlot(j, d.name, d.area)).filter((p): p is MatrixPoint => p !== null));

  const filtered = allPoints.filter(p => {
    if (filterArea !== 'All' && p.area !== filterArea) return false;
    if (filterDomain !== 'All' && p.domain !== filterDomain) return false;
    if (filterPhase !== 'All' && p.phase !== filterPhase) return false;
    if (filterApproach !== 'All' && p.approach !== filterApproach) return false;
    if (filterQuadrant !== 'All' && quadrantOf(p) !== filterQuadrant) return false;
    return true;
  });

  const hovered = allPoints.find(p => p.id === hoveredId);

  // Group filtered points by (x,y) and assign jitter offsets.
  const posGroups: Record<string, string[]> = {};
  filtered.forEach(p => {
    const key = `${p.x},${p.y}`;
    if (!posGroups[key]) posGroups[key] = [];
    posGroups[key].push(p.id);
  });
  const jitterMap: Record<string, {dx: number; dy: number}> = {};
  Object.values(posGroups).forEach(ids => {
    ids.forEach((id, idx) => {
      jitterMap[id] = jitterOffset(idx, ids.length);
    });
  });

  const QUADRANTS = [
    {x: 3, y: 3, w: 3, h: 3, label: 'Act Now', sub: 'High disruption · High readiness', bg: '#f0fdf4', border: '#86efac'},
    {x: 3, y: 0, w: 3, h: 3, label: 'Invest to Unlock', sub: 'High disruption · Low readiness', bg: '#fff7ed', border: '#fed7aa'},
    {x: 0, y: 3, w: 3, h: 3, label: 'Ready but Low Impact', sub: 'Low disruption · High readiness', bg: '#f0f9ff', border: '#bae6fd'},
    {x: 0, y: 0, w: 3, h: 3, label: 'Monitor', sub: 'Low disruption · Low readiness', bg: '#fafafa', border: '#e5e7eb'}
  ];

  const hlColor: Record<string, string> = {High: '#DC2626', Medium: '#D97706', Low: '#16A34A'};
  const fundColor: Record<string, string> = {Funded: '#16A34A', Partial: '#D97706', Unfunded: '#DC2626'};

  const phaseOptions = ['All', ...phases];
  const approachOptions = ['All', 'Buy', 'Build', 'Both', 'Mix'];

  const domainsWithSC = domains.filter(d => d.jtbds.some(hasStrategyContext)).length;

  if (allPoints.length === 0) {
    return (
      <div style={{textAlign: 'center', padding: '80px 40px', color: '#94A3B8'}}>
        <div style={{fontSize: 32, marginBottom: 16}}>📊</div>
        <div style={{fontSize: 16, fontWeight: 600, color: '#334155', marginBottom: 8}}>No strategy context data yet</div>
        <div style={{fontSize: 13}}>The 2×2 plots once Follow-Up Skill docs have been loaded with Strategy Context fields populated.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{marginBottom: 20}}>
        <h2 style={{fontSize: 20, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em', marginBottom: 4}}>
          JTBD Prioritization Matrix
        </h2>
        <p style={{fontSize: 13, color: '#64748B'}}>
          Disruption Potential × Implementation Readiness · {allPoints.length} JTBDs from {domainsWithSC} domains with Follow-Up complete
        </p>
      </div>

      {/* Filters */}
      <div style={{display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
          <span style={{fontSize: 11, color: '#94A3B8', fontFamily: "'DM Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em'}}>
            SA
          </span>
          {serviceAreas.map(sa => {
            const active = filterArea === sa.id;
            const saJtbdCount = allPoints.filter(p => p.area === sa.id).length;
            if (saJtbdCount === 0) return null;
            return (
              <button
                key={sa.id}
                onClick={() => {
                  setFilterArea(active ? 'All' : sa.id);
                  setFilterDomain('All');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  background: active ? sa.color + '18' : '#fff',
                  border: `1.5px solid ${active ? sa.color : '#E2E8F0'}`,
                  color: active ? sa.color : '#64748B',
                  transition: 'all 0.12s'
                }}
              >
                <div style={{width: 7, height: 7, borderRadius: '50%', background: sa.color}} />
                {sa.id}
                <span style={{fontSize: 9, color: active ? sa.color : '#94A3B8', fontFamily: "'DM Mono', monospace"}}>{saJtbdCount}</span>
              </button>
            );
          })}
        </div>
        {filterArea !== 'All' &&
          (() => {
            const areaDomains = [...new Set(allPoints.filter(p => p.area === filterArea).map(p => p.domain))].sort();
            if (areaDomains.length <= 1) return null;
            return (
              <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <span style={{fontSize: 11, color: '#94A3B8', fontFamily: "'DM Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em'}}>
                  Domain
                </span>
                <select
                  value={filterDomain}
                  onChange={e => setFilterDomain(e.target.value)}
                  style={{padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff', fontSize: 12, color: '#334155', cursor: 'pointer', maxWidth: 220}}
                >
                  <option value="All">All ({areaDomains.length})</option>
                  {areaDomains.map(d => {
                    const count = allPoints.filter(p => p.area === filterArea && p.domain === d).length;
                    return (
                      <option key={d} value={d}>
                        {d} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            );
          })()}
        {[
          {label: 'Phase', val: filterPhase, set: setFilterPhase, opts: phaseOptions},
          {label: 'Approach', val: filterApproach, set: setFilterApproach, opts: approachOptions}
        ].map(f => (
          <div key={f.label} style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <span style={{fontSize: 11, color: '#94A3B8', fontFamily: "'DM Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em'}}>
              {f.label}
            </span>
            <select
              value={f.val}
              onChange={e => f.set(e.target.value)}
              style={{padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#fff', fontSize: 12, color: '#334155', cursor: 'pointer'}}
            >
              {f.opts.map(o => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
        ))}
        <span style={{fontSize: 12, color: '#94A3B8', fontFamily: "'DM Mono', monospace"}}>{filtered.length} JTBDs shown</span>
      </div>

      {/* Quadrant summary bar */}
      {(() => {
        const preQuadrantFiltered = allPoints.filter(p => {
          if (filterArea !== 'All' && p.area !== filterArea) return false;
          if (filterDomain !== 'All' && p.domain !== filterDomain) return false;
          if (filterPhase !== 'All' && p.phase !== filterPhase) return false;
          if (filterApproach !== 'All' && p.approach !== filterApproach) return false;
          return true;
        });
        const qCounts: Record<string, number> = {'Act Now': 0, 'Invest to Unlock': 0, 'Ready but Low Impact': 0, Monitor: 0};
        preQuadrantFiltered.forEach(p => {
          qCounts[quadrantOf(p)]++;
        });
        const qMeta = [
          {key: 'Act Now', accent: '#16A34A', bg: '#f0fdf4', border: '#86efac', icon: '🎯'},
          {key: 'Invest to Unlock', accent: '#EA580C', bg: '#fff7ed', border: '#fed7aa', icon: '🔓'},
          {key: 'Ready but Low Impact', accent: '#0284C7', bg: '#f0f9ff', border: '#bae6fd', icon: '⏳'},
          {key: 'Monitor', accent: '#6B7280', bg: '#fafafa', border: '#e5e7eb', icon: '👁'}
        ];
        return (
          <div style={{display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap'}}>
            {qMeta.map(q => {
              const isActive = filterQuadrant === q.key;
              return (
                <div
                  key={q.key}
                  onClick={() => setFilterQuadrant(isActive ? 'All' : q.key)}
                  style={{
                    background: q.bg,
                    border: `${isActive ? '2px' : '1px'} solid ${isActive ? q.accent : q.border}`,
                    borderRadius: 10,
                    padding: isActive ? '9px 15px' : '10px 16px',
                    minWidth: 150,
                    flex: 1,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: isActive ? `0 2px 12px ${q.accent}30` : 'none'
                  }}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
                    <span style={{fontSize: 13}}>{q.icon}</span>
                    <span style={{fontSize: 11, fontWeight: 700, color: q.accent, textTransform: 'uppercase', letterSpacing: '0.04em'}}>{q.key}</span>
                  </div>
                  <div style={{fontSize: 28, fontWeight: 800, color: q.accent, fontFamily: "'DM Mono', monospace", letterSpacing: '-0.02em'}}>
                    {qCounts[q.key]}
                  </div>
                  <div style={{fontSize: 10, color: '#94A3B8', marginTop: 2}}>
                    {preQuadrantFiltered.length ? Math.round((qCounts[q.key] / preQuadrantFiltered.length) * 100) : 0}% of total
                  </div>
                </div>
              );
            })}
            {filterQuadrant !== 'All' && (
              <button
                onClick={() => setFilterQuadrant('All')}
                style={{
                  alignSelf: 'center',
                  background: '#F1F5F9',
                  border: '1px solid #E2E8F0',
                  color: '#64748B',
                  borderRadius: 8,
                  padding: '6px 14px',
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace"
                }}
              >
                ✕ Clear
              </button>
            )}
          </div>
        );
      })()}

      <div style={{display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap'}}>
        {/* Chart */}
        <div ref={chartRef} style={{position: 'relative', flexShrink: 0}}>
          <svg width={CHART_SIZE} height={CHART_SIZE} style={{display: 'block'}}>
            {QUADRANTS.map((q, i) => (
              <g key={i}>
                <rect
                  x={PADDING + toPixel(q.x)}
                  y={PADDING + PLOT_SIZE - toPixel(q.y + q.h)}
                  width={toPixel(q.w)}
                  height={toPixel(q.h)}
                  fill={q.bg}
                  stroke={q.border}
                  strokeWidth={1}
                />
                <text
                  x={PADDING + toPixel(q.x) + toPixel(q.w) / 2}
                  y={PADDING + PLOT_SIZE - toPixel(q.y + q.h) + 18}
                  textAnchor="middle"
                  fill="#94A3B8"
                  fontSize={11}
                  fontWeight={700}
                  style={{textTransform: 'uppercase', letterSpacing: '0.08em'}}
                >
                  {q.label}
                </text>
                <text x={PADDING + toPixel(q.x) + toPixel(q.w) / 2} y={PADDING + PLOT_SIZE - toPixel(q.y + q.h) + 32} textAnchor="middle" fill="#CBD5E1" fontSize={9.5}>
                  {q.sub}
                </text>
              </g>
            ))}
            {[1, 2, 3, 4, 5].map(i => (
              <g key={i}>
                <line x1={PADDING + toPixel(i)} y1={PADDING} x2={PADDING + toPixel(i)} y2={PADDING + PLOT_SIZE} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4,4" />
                <line x1={PADDING} y1={PADDING + PLOT_SIZE - toPixel(i)} x2={PADDING + PLOT_SIZE} y2={PADDING + PLOT_SIZE - toPixel(i)} stroke="#E2E8F0" strokeWidth={1} strokeDasharray="4,4" />
              </g>
            ))}
            <line x1={PADDING} y1={PADDING + PLOT_SIZE} x2={PADDING + PLOT_SIZE} y2={PADDING + PLOT_SIZE} stroke="#CBD5E1" strokeWidth={1.5} />
            <line x1={PADDING} y1={PADDING} x2={PADDING} y2={PADDING + PLOT_SIZE} stroke="#CBD5E1" strokeWidth={1.5} />
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <g key={i}>
                <text x={PADDING + toPixel(i)} y={PADDING + PLOT_SIZE + 16} textAnchor="middle" fill="#94A3B8" fontSize={10}>
                  {i}
                </text>
                <text x={PADDING - 14} y={PADDING + PLOT_SIZE - toPixel(i) + 4} textAnchor="middle" fill="#94A3B8" fontSize={10}>
                  {i}
                </text>
              </g>
            ))}
            <text x={PADDING + PLOT_SIZE / 2} y={CHART_SIZE - 6} textAnchor="middle" fill="#475569" fontSize={12} fontWeight={600}>
              Disruption Potential →
            </text>
            <text transform={`translate(14, ${PADDING + PLOT_SIZE / 2}) rotate(-90)`} textAnchor="middle" fill="#475569" fontSize={12} fontWeight={600}>
              Implementation Readiness →
            </text>

            {/* Dots */}
            {filtered.map(p => {
              const jitter = jitterMap[p.id] || {dx: 0, dy: 0};
              const cx = PADDING + toPixel(p.x) + jitter.dx;
              const cy = PADDING + PLOT_SIZE - toPixel(p.y) + jitter.dy;
              const r = (effortSize[p.hci ?? ''] || 14) / 2;
              const phaseColor = phaseColors[p.phase]?.dot || '#94A3B8';
              const isHovered = hoveredId === p.id;
              const hoveredArea = hovered ? hovered.area : null;
              const dimmed = hoveredArea && p.area !== hoveredArea && !isHovered;
              const cm = complexityMeta(p.complexity);
              const domainObj = domains.find(d => d.name === p.domain);
              return (
                <g
                  key={p.id}
                  onMouseMove={e => {
                    const rect = chartRef.current ? chartRef.current.getBoundingClientRect() : null;
                    if (!rect) return;
                    setHoveredId(p.id);
                    setHoverPos({x: e.clientX - rect.left, y: e.clientY - rect.top});
                  }}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => {
                    if (domainObj) setSelectedDomain(domainObj);
                  }}
                  style={{cursor: 'pointer'}}
                >
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r + (isHovered ? 3 : 0)}
                    fill={phaseColor}
                    fillOpacity={dimmed ? 0.15 : isHovered ? 1 : 0.75}
                    stroke={dimmed ? 'transparent' : '#fff'}
                    strokeWidth={2}
                    style={{transition: 'r 0.15s, fill-opacity 0.15s'}}
                  />
                  {!dimmed && (
                    <foreignObject x={cx + r - 2} y={cy - r - 12} width={28} height={14} style={{overflow: 'visible'}}>
                      <div
                        style={{
                          background: cm.bg,
                          color: cm.text,
                          border: `1px solid ${cm.border}`,
                          borderRadius: 3,
                          fontSize: p.complexity === 'MA' ? 6 : 7,
                          fontWeight: 800,
                          padding: '1px 3px',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}
                      >
                        {p.complexity}
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Hover card */}
          {hovered && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(hoverPos.x + 14, CHART_SIZE - 260),
                top: Math.max(hoverPos.y - 10, 0),
                width: 250,
                background: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: 10,
                padding: '12px 14px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                pointerEvents: 'none',
                zIndex: 100
              }}
            >
              <div style={{fontSize: 12, fontWeight: 700, color: '#0F172A', marginBottom: 2, lineHeight: 1.4}}>{hovered.jtbd}</div>
              <div style={{fontSize: 11, color: '#64748B', marginBottom: 10}}>
                {hovered.domain} · {hovered.area}
              </div>
              <div style={{display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap'}}>
                <PhasePill phase={hovered.phase} small />
                <span style={{fontSize: 10, padding: '2px 7px', borderRadius: 4, background: '#F1F5F9', color: '#475569', fontWeight: 600}}>{hovered.approach}</span>
              </div>
              <div style={{display: 'grid', gap: 6}}>
                {[
                  {label: 'Manual Effort', value: hovered.manualEffort, color: hlColor[hovered.manualEffort ?? '']},
                  {label: 'Leader Opportunity', value: hovered.leaderOpportunity, color: hlColor[hovered.leaderOpportunity ?? '']},
                  {label: 'Tech Readiness', value: hovered.techReadiness, color: null},
                  {label: 'Funding', value: hovered.fundingStatus, color: fundColor[hovered.fundingStatus ?? '']},
                  {label: 'Human Capital', value: hovered.hci, color: hlColor[hovered.hci ?? '']},
                  {
                    label: 'Complexity',
                    value: `${hovered.complexity} — ${complexityMeta(hovered.complexity).label}`,
                    color: COMPLEXITY_META[hovered.complexity as ComplexityCode]?.text
                  }
                ].map(row => (
                  <div key={row.label} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <span style={{fontSize: 10, color: '#94A3B8', fontWeight: 600}}>{row.label}</span>
                    <span style={{fontSize: 10, fontWeight: 700, color: row.color || '#334155'}}>{row.value || '—'}</span>
                  </div>
                ))}
              </div>
              <div style={{marginTop: 10, paddingTop: 8, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-around'}}>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: 20, fontWeight: 800, color: '#3B82F6'}}>{hovered.x}</div>
                  <div style={{fontSize: 9, color: '#94A3B8', fontWeight: 600}}>/6 Disruption</div>
                </div>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: 20, fontWeight: 800, color: '#10B981'}}>{hovered.y}</div>
                  <div style={{fontSize: 9, color: '#94A3B8', fontWeight: 600}}>/6 Readiness</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Guide Panel */}
        <div style={{background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 18px', minWidth: 220, maxWidth: 240}}>
          <div style={{fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14}}>Guide</div>

          <div style={{padding: '8px 10px', background: '#F8FAFC', borderRadius: 7, marginBottom: 14, fontSize: 10, color: '#64748B', lineHeight: 1.6}}>
            This chart plots JTBDs that leaders identified as <b style={{color: '#475569'}}>high-value transformation candidates</b> — so clustering toward higher
            disruption is expected, not a sign something is wrong. Use it to prioritize <em>within</em> that set, not to assess the full landscape.
          </div>

          <div style={{fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 8}}>Quadrants</div>
          {[
            {label: 'Act Now', bg: '#f0fdf4', border: '#86efac', text: '#15803d', action: 'High disruption + ready to move. Prioritize immediately.'},
            {label: 'Invest to Unlock', bg: '#fff7ed', border: '#fed7aa', text: '#c2410c', action: 'High disruption but not ready. Invest in technology or funding to unlock.'},
            {label: 'Ready but Low Impact', bg: '#f0f9ff', border: '#bae6fd', text: '#0369a1', action: 'Tools exist but disruption is low. Proceed only if capacity allows.'},
            {label: 'Monitor', bg: '#fafafa', border: '#e5e7eb', text: '#6b7280', action: "Low disruption and low readiness. Watch but don't invest yet."}
          ].map(q => (
            <div key={q.label} style={{marginBottom: 8, padding: '7px 9px', background: q.bg, border: `1px solid ${q.border}`, borderRadius: 6}}>
              <div style={{fontSize: 10, fontWeight: 800, color: q.text, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em'}}>{q.label}</div>
              <div style={{fontSize: 10, color: '#64748B', lineHeight: 1.5}}>{q.action}</div>
            </div>
          ))}

          <div style={{fontSize: 11, fontWeight: 700, color: '#475569', marginTop: 14, marginBottom: 8}}>How scores are calculated</div>
          <div style={{padding: '9px 10px', background: '#F8FAFC', borderRadius: 7, marginBottom: 6}}>
            <div style={{fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 5}}>
              X — Disruption Potential <span style={{fontWeight: 400, color: '#94A3B8'}}>(max 6)</span>
            </div>
            {[
              {input: 'Manual Effort', scores: 'H=3 · M=2 · L=1'},
              {input: 'Leader Opportunity', scores: 'H=3 · M=2 · L=1'}
            ].map(r => (
              <div key={r.input} style={{display: 'flex', justifyContent: 'space-between', marginBottom: 3}}>
                <span style={{fontSize: 10, color: '#64748B'}}>{r.input}</span>
                <span style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace"}}>{r.scores}</span>
              </div>
            ))}
          </div>
          <div style={{padding: '9px 10px', background: '#F8FAFC', borderRadius: 7}}>
            <div style={{fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 5}}>
              Y — Implementation Readiness <span style={{fontWeight: 400, color: '#94A3B8'}}>(max 6)</span>
            </div>
            {[
              {input: 'Tech Readiness', scores: 'H=3 · M=2 · L=1'},
              {input: 'Funding Status', scores: 'Funded=3 · Partial=2 · Unfunded=1'}
            ].map(r => (
              <div key={r.input} style={{display: 'flex', justifyContent: 'space-between', marginBottom: 3}}>
                <span style={{fontSize: 10, color: '#64748B'}}>{r.input}</span>
                <span style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace"}}>{r.scores}</span>
              </div>
            ))}
          </div>

          <div style={{fontSize: 11, fontWeight: 700, color: '#475569', marginTop: 14, marginBottom: 8}}>What each dot tells you</div>
          <div style={{fontSize: 10, color: '#64748B', marginBottom: 6, lineHeight: 1.5}}>
            <b>Color</b> — AIT phase the JTBD is currently in
          </div>
          {phases.map(p => (
            <div key={p} style={{display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5}}>
              <div style={{width: 11, height: 11, borderRadius: '50%', background: phaseColors[p]?.dot, flexShrink: 0}} />
              <span style={{fontSize: 11, color: '#334155'}}>{p}</span>
            </div>
          ))}
          <div style={{fontSize: 10, color: '#64748B', marginTop: 8, marginBottom: 6, lineHeight: 1.5}}>
            <b>Size</b> — Human capital impact (how much team time this job consumes)
          </div>
          {([['High', 22], ['Medium', 14], ['Low', 8]] as [string, number][]).map(([label, size]) => (
            <div key={label} style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5}}>
              <div style={{width: size, height: size, borderRadius: '50%', background: '#94A3B8', flexShrink: 0}} />
              <span style={{fontSize: 11, color: '#334155'}}>{label}</span>
            </div>
          ))}
          <div style={{fontSize: 10, color: '#64748B', marginTop: 8, marginBottom: 6, lineHeight: 1.5}}>
            <b>Badge</b> — Implementation complexity
          </div>
          {(Object.entries(COMPLEXITY_META) as [ComplexityCode, (typeof COMPLEXITY_META)[ComplexityCode]][]).map(([code, c]) => (
            <div key={code} style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5}}>
              <div
                style={{
                  minWidth: 24,
                  padding: '1px 4px',
                  borderRadius: 3,
                  background: c.bg,
                  color: c.text,
                  border: `1px solid ${c.border}`,
                  fontSize: code === 'MA' ? 7 : 8,
                  fontWeight: 800,
                  textAlign: 'center'
                }}
              >
                {code}
              </div>
              <span style={{fontSize: 11, color: '#334155'}}>{c.label}</span>
            </div>
          ))}

          <div style={{marginTop: 14, padding: '8px 10px', background: '#F8FAFC', borderRadius: 7, fontSize: 10, color: '#94A3B8', lineHeight: 1.5}}>
            💡 Hover any dot to see all nine strategy inputs. Click any dot to open the domain detail.
          </div>
        </div>
      </div>

      {/* Quadrant detail list */}
      {filterQuadrant !== 'All' &&
        filtered.length > 0 &&
        (() => {
          const qMetaMap: Record<string, {accent: string; bg: string; border: string; desc: string}> = {
            'Act Now': {accent: '#16A34A', bg: '#f0fdf4', border: '#86efac', desc: 'High disruption + high readiness — these are ready to move.'},
            'Invest to Unlock': {accent: '#EA580C', bg: '#fff7ed', border: '#fed7aa', desc: 'High disruption but blocked — what needs to change to unlock these?'},
            'Ready but Low Impact': {accent: '#0284C7', bg: '#f0f9ff', border: '#bae6fd', desc: 'Ready to go but lower disruption potential. Proceed if capacity allows.'},
            Monitor: {accent: '#6B7280', bg: '#fafafa', border: '#e5e7eb', desc: 'Low disruption and low readiness. Watch for changes.'}
          };
          const qm = qMetaMap[filterQuadrant];
          const isInvest = filterQuadrant === 'Invest to Unlock';

          function blockerTag(p: MatrixPoint): {label: string; bg: string; color: string; border: string} {
            const tr = H_M_L[p.techReadiness ?? ''] || 1;
            const fs = FUND_MAP[p.fundingStatus ?? ''] || 1;
            if (tr <= 1 && fs <= 1) return {label: 'Needs Both', bg: '#FEF2F2', color: '#DC2626', border: '#FECACA'};
            if (fs <= 1) return {label: 'Needs Funding', bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA'};
            if (tr <= 1) return {label: 'Needs Tech', bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE'};
            return {label: 'Partial', bg: '#FFFBEB', color: '#D97706', border: '#FDE68A'};
          }

          const bySA: Record<string, Record<string, MatrixPoint[]>> = {};
          filtered.forEach(p => {
            const area = p.area ?? '—';
            if (!bySA[area]) bySA[area] = {};
            if (!bySA[area][p.domain]) bySA[area][p.domain] = [];
            bySA[area][p.domain].push(p);
          });

          let blockerSummary: Record<string, number> | null = null;
          if (isInvest) {
            const counts: Record<string, number> = {'Needs Funding': 0, 'Needs Tech': 0, 'Needs Both': 0, Partial: 0};
            filtered.forEach(p => {
              counts[blockerTag(p).label]++;
            });
            blockerSummary = counts;
          }

          return (
            <div style={{marginTop: 20, background: '#fff', border: `1px solid ${qm.border}`, borderRadius: 12, overflow: 'hidden'}}>
              <div
                style={{
                  padding: '14px 20px',
                  background: qm.bg,
                  borderBottom: `1px solid ${qm.border}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12
                }}
              >
                <div>
                  <div style={{fontSize: 14, fontWeight: 700, color: qm.accent, marginBottom: 2}}>
                    {filterQuadrant} — {filtered.length} JTBDs
                  </div>
                  <div style={{fontSize: 11, color: '#64748B'}}>{qm.desc}</div>
                </div>
                {blockerSummary && (
                  <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                    {Object.entries(blockerSummary)
                      .filter(([, c]) => c > 0)
                      .map(([label, count]) => {
                        const bt = {
                          'Needs Funding': {bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA'},
                          'Needs Tech': {bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE'},
                          'Needs Both': {bg: '#FEF2F2', color: '#DC2626', border: '#FECACA'},
                          Partial: {bg: '#FFFBEB', color: '#D97706', border: '#FDE68A'}
                        }[label]!;
                        return (
                          <span key={label} style={{fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: bt.bg, color: bt.color, border: `1px solid ${bt.border}`}}>
                            {count} {label}
                          </span>
                        );
                      })}
                  </div>
                )}
              </div>
              <div style={{padding: '12px 20px', maxHeight: 420, overflowY: 'auto'}}>
                {Object.entries(bySA)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([sa, saDomains]) => {
                    const saColor = serviceAreas.find(s => s.id === sa)?.color || '#94A3B8';
                    return (
                      <div key={sa} style={{marginBottom: 16}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8}}>
                          <div style={{width: 8, height: 8, borderRadius: '50%', background: saColor}} />
                          <span style={{fontSize: 11, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.06em'}}>{sa}</span>
                          <span style={{fontSize: 10, color: '#94A3B8'}}>{Object.values(saDomains).flat().length} JTBDs</span>
                        </div>
                        {Object.entries(saDomains)
                          .sort((a, b) => b[1].length - a[1].length)
                          .map(([domName, pts]) => (
                            <div key={domName} style={{marginLeft: 14, marginBottom: 10}}>
                              <div style={{fontSize: 12, fontWeight: 600, color: '#0F172A', marginBottom: 4}}>
                                {domName} <span style={{fontWeight: 400, color: '#94A3B8', fontSize: 11}}>· {domains.find(d => d.name === domName)?.leader}</span>
                              </div>
                              <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
                                {pts.map(p => {
                                  const bt = isInvest ? blockerTag(p) : null;
                                  return (
                                    <div key={p.id} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#F8FAFC', borderRadius: 6, border: '1px solid #F1F5F9'}}>
                                      <span style={{fontSize: 11, color: '#334155', flex: 1}}>{p.jtbd}</span>
                                      {isInvest && bt && (
                                        <span style={{fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: bt.bg, color: bt.color, border: `1px solid ${bt.border}`, whiteSpace: 'nowrap'}}>
                                          {bt.label}
                                        </span>
                                      )}
                                      <span style={{fontSize: 9, fontWeight: 600, color: fundColor[p.fundingStatus ?? ''] || '#94A3B8', whiteSpace: 'nowrap'}}>{p.fundingStatus}</span>
                                      <span style={{fontSize: 9, color: '#94A3B8', whiteSpace: 'nowrap'}}>Tech: {p.techReadiness}</span>
                                      <span style={{fontSize: 9, color: '#94A3B8', whiteSpace: 'nowrap'}}>HCI: {p.hci}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })()}

      {selectedDomain && <DomainDetail domain={selectedDomain} onClose={() => setSelectedDomain(null)} />}
    </div>
  );
}
